import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { requestAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { FolderIcon, FolderOpenIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';

const ResolvedRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedRequests, setGroupedRequests] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});

  useEffect(() => {
    fetchResolvedRequests();
  }, []);

  const fetchResolvedRequests = async () => {
    setLoading(true);
    try {
      // Fetch resolved and closed requests
      const resolvedResponse = await requestAPI.getAll({ status: 'resolved' });
      const closedResponse = await requestAPI.getAll({ status: 'closed' });
      const allResolved = [...resolvedResponse.data, ...closedResponse.data];

      // Sort by resolvedAt date
      allResolved.sort((a, b) => {
        const dateA = new Date(a.resolvedAt || a.closedAt || a.updatedAt);
        const dateB = new Date(b.resolvedAt || b.closedAt || b.updatedAt);
        return dateB - dateA;
      });

      setRequests(allResolved);

      // Group by month/year
      const grouped = {};
      allResolved.forEach(request => {
        const date = new Date(request.resolvedAt || request.closedAt || request.updatedAt);
        const key = format(date, 'MMMM yyyy', { locale: tr });
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(request);
      });

      setGroupedRequests(grouped);

      // Expand current month by default
      const currentMonth = format(new Date(), 'MMMM yyyy', { locale: tr });
      setExpandedFolders({ [currentMonth]: true });
    } catch (error) {
      toast.error('Çözülen talepler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (key) => {
    setExpandedFolders(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Düşük' },
      medium: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Orta' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Yüksek' },
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Kritik' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <DocumentCheckIcon className="h-8 w-8 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Çözülen İşlemler</h1>
            <p className="text-gray-500">Toplam {requests.length} çözülmüş talep</p>
          </div>
        </div>
      </div>

      {Object.keys(groupedRequests).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <DocumentCheckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Henüz çözülmüş talep bulunmuyor</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedRequests).map(([monthYear, monthRequests]) => (
            <div key={monthYear} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Folder Header */}
              <button
                onClick={() => toggleFolder(monthYear)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedFolders[monthYear] ? (
                    <FolderOpenIcon className="h-6 w-6 text-yellow-500" />
                  ) : (
                    <FolderIcon className="h-6 w-6 text-yellow-500" />
                  )}
                  <span className="font-semibold text-gray-900 capitalize">{monthYear}</span>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    {monthRequests.length} talep
                  </span>
                </div>
                <svg
                  className={`h-5 w-5 text-gray-400 transition-transform ${expandedFolders[monthYear] ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Folder Contents */}
              {expandedFolders[monthYear] && (
                <div className="border-t">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Talep</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tür</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Talep Eden</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Çözen</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Öncelik</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Çözüm Tarihi</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monthRequests.map((request, index) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{request.title}</div>
                            {request.resolution && (
                              <div className="text-sm text-green-600 truncate max-w-xs">
                                ✓ {request.resolution}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${request.requestType?.category === 'issue' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                              {request.requestType?.name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.requester?.fullName}</div>
                            <div className="text-xs text-gray-500">{request.requester?.department}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.resolvedBy?.fullName || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getPriorityBadge(request.priority)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(request.resolvedAt || request.closedAt || request.updatedAt), 'dd.MM.yyyy HH:mm')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Link
                              to={`/requests/${request.id}`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Detay
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResolvedRequests;
